import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Badge,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material"
import SendIcon from "@mui/icons-material/Send"
import UndoIcon from "@mui/icons-material/Undo"
import CloseIcon from "@mui/icons-material/Close"
import TouchBoard, { placementAtClientPoint } from "../components/TouchBoard"
import { useBoardConnection } from "../components/BoardConnection"
import RoleBar, { ROLE_MIDDLE, roleRgb } from "../components/RoleBar"
import GymSlotPicker from "../components/GymSlotPicker"
import useStickyState from "../hooks/useStickyState"
import { displayAtGym } from "../utils/gymApi"
import {
  clearWall,
  colorByteToRgb,
  PALETTE_A,
  sendClimbsToWall,
  withAliases,
} from "../utils/bluetooth"

const API_BASE = process.env.REACT_APP_API_BASE_URL || ""

// The hold under the finger lights white on the wall so it stands out from
// the holds already committed to the project.
const CANDIDATE_BYTE = 252
const CANDIDATE_PALETTE = withAliases({ MIDDLE: CANDIDATE_BYTE })
const CANDIDATE_RGB = colorByteToRgb(CANDIDATE_BYTE)

// Layout's sticky app bar plus its content padding, by breakpoint. The page
// is sized to exactly the rest of the viewport so nothing can scroll.
const CHROME_HEIGHT = { xs: 56 + 16 * 2, md: 64 + 24 * 2 }

const toClimb = (entries) => ({
  placements: entries.map(([p]) => ({ led_position: p.led_position })),
  placement_roles: entries.map(([, role]) => role),
})

export default function SetProject() {
  const [placements, setPlacements] = useState([])
  // placement_id -> role id
  const [selected, setSelected] = useState(() => new Map())
  // Previous `selected` maps, most recent last, for undo
  const [history, setHistory] = useState([])
  const [candidate, setCandidate] = useState(null)
  const [activeRole, setActiveRole] = useStickyState(
    ROLE_MIDDLE,
    "setProjectRole"
  )
  const { characteristic: bluetoothCharacteristic } = useBoardConnection()
  const boardRef = useRef(null)
  const surfaceRef = useRef(null)
  // The in-progress press: which role it will commit, and the last
  // candidate so moves only re-render when the nearest hold changes.
  const dragRef = useRef(null)
  // Latest-wins queue: BLE writes must be serialized, and while dragging
  // the candidate can change faster than the wall accepts frames.
  const sendQueueRef = useRef({ inFlight: false, pending: null })

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/hold-color-mapping`)
      .then((r) => r.json())
      .then((data) => {
        setPlacements(
          (data.placements || []).filter(
            (p) => p.in_bounds && p.led_position != null
          )
        )
      })
      .catch((err) => console.error("Failed to load placements:", err))
  }, [])

  const pushToWall = useCallback(
    (entries) => {
      if (!bluetoothCharacteristic) {
        return
      }
      const queue = sendQueueRef.current
      queue.pending = entries
      if (queue.inFlight) {
        return
      }
      queue.inFlight = true
      const drain = async () => {
        while (queue.pending) {
          const next = queue.pending
          queue.pending = null
          try {
            await sendClimbsToWall(bluetoothCharacteristic, next)
          } catch (err) {
            console.error("Failed to send to wall:", err)
          }
        }
        queue.inFlight = false
      }
      drain()
    },
    [bluetoothCharacteristic]
  )

  // Mirror phone state onto the wall: candidate first so it wins the LED
  // when the finger is over an already-selected hold.
  useEffect(() => {
    if (!bluetoothCharacteristic) {
      return
    }
    const byId = new Map(placements.map((p) => [p.placement_id, p]))
    const committed = Array.from(selected, ([id, role]) => [byId.get(id), role])
      .filter(([p]) => p)
    const entries = []
    if (candidate) {
      entries.push({
        climb: toClimb([[candidate, ROLE_MIDDLE]]),
        palette: CANDIDATE_PALETTE,
      })
    }
    entries.push({ climb: toClimb(committed), palette: PALETTE_A })
    pushToWall(entries)
  }, [bluetoothCharacteristic, placements, selected, candidate, pushToWall])

  // Every change to `selected` goes through here so undo can restore the
  // state it replaced. History is capped to bound memory.
  const applySelected = (next) => {
    setHistory((h) => [...h.slice(-99), selected])
    setSelected(next)
  }

  // Releasing on a hold: give it the active role; if it already has that
  // role, drop it.
  const commit = (placement, role) => {
    const next = new Map(selected)
    if (next.get(placement.placement_id) === role) {
      next.delete(placement.placement_id)
    } else {
      next.set(placement.placement_id, role)
    }
    applySelected(next)
  }

  const clearSelection = () => {
    if (selected.size > 0) {
      applySelected(new Map())
    }
  }

  const undo = () => {
    if (history.length === 0) {
      return
    }
    setSelected(history[history.length - 1])
    setHistory(history.slice(0, -1))
  }

  const updateCandidate = (event) => {
    const drag = dragRef.current
    const next = placementAtClientPoint(
      boardRef.current,
      placements,
      event.clientX,
      event.clientY
    )
    if (next?.placement_id !== drag.candidate?.placement_id) {
      drag.candidate = next
      setCandidate(next)
    }
    return next
  }

  // One pointer surface covers the board and the role bar, so a press on a
  // role swatch both selects that role and can be dragged up into the image.
  const handlePointerDown = (event) => {
    if (event.button != null && event.button !== 0) {
      return
    }
    const swatch = event.target.closest?.("[data-role]")
    let role = activeRole
    if (swatch) {
      role = Number(swatch.dataset.role)
      setActiveRole(role)
    } else if (
      !placementAtClientPoint(
        boardRef.current,
        placements,
        event.clientX,
        event.clientY
      )
    ) {
      return
    }
    event.preventDefault()
    surfaceRef.current?.setPointerCapture?.(event.pointerId)
    dragRef.current = { role, candidate: null }
    updateCandidate(event)
  }

  const handlePointerMove = (event) => {
    if (!dragRef.current) {
      return
    }
    event.preventDefault()
    updateCandidate(event)
  }

  const handlePointerUp = (event) => {
    const drag = dragRef.current
    if (!drag) {
      return
    }
    event.preventDefault()
    // Use the final finger position rather than the last move event
    const final = updateCandidate(event)
    dragRef.current = null
    setCandidate(null)
    if (final) {
      commit(final, drag.role)
    }
  }

  const handlePointerCancel = () => {
    dragRef.current = null
    setCandidate(null)
  }

  const selectedRings = new Map(
    Array.from(selected, ([id, role]) => [id, roleRgb(role)])
  )

  return (
    <Box
      sx={{
        height: {
          xs: `calc(100dvh - ${CHROME_HEIGHT.xs}px)`,
          md: `calc(100dvh - ${CHROME_HEIGHT.md}px)`,
        },
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "hidden",
        overscrollBehavior: "none",
        touchAction: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
          Set by touch
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {bluetoothCharacteristic && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => {
                clearSelection()
                clearWall(bluetoothCharacteristic).catch((err) =>
                  console.error("Failed to blank wall:", err)
                )
              }}
            >
              Blank board
            </Button>
          )}
          <GymSlotPicker
            iconOnly
            icon={<SendIcon />}
            buttonLabel="Send to gym…"
            disabled={selected.size === 0}
            onConfirm={async (slug, slot) => {
              const frames = Array.from(selected, ([id, role]) => `p${id}r${role}`)
                .join("")
              await displayAtGym(slug, slot, {
                type: "custom",
                frames,
                name: "Touch-set project",
              })
            }}
          />
          <Tooltip title="Undo">
            <span>
              <IconButton
                size="small"
                aria-label="Undo"
                onClick={undo}
                disabled={history.length === 0}
              >
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear">
            <span>
              <IconButton
                size="small"
                aria-label="Clear"
                onClick={clearSelection}
                disabled={selected.size === 0}
              >
                <Badge
                  badgeContent={selected.size}
                  color="primary"
                  sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem" } }}
                >
                  <CloseIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        ref={surfaceRef}
        data-testid="touch-surface"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          touchAction: "none",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <TouchBoard
            boardRef={boardRef}
            placements={placements}
            selected={selectedRings}
            candidate={candidate}
            candidateColor={CANDIDATE_RGB}
          />
        </Box>
        <RoleBar value={activeRole} />
      </Box>
    </Box>
  )
}
